import { InvestigationStatus } from '@customer/store/apis/investigationsApi';
import styles from './InvestigationStatusHeader.module.scss';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useState } from 'react';
import { TranslationNamespaceBackend } from '@cas/utils/translationHelpers';
import Dropdown from '@cas/components/inputs/dropdown/Dropdown';
import { DropdownItemProps } from '@cas/components/inputs/dropdown-items/DropdownItems';
import { useToggle } from '@customer/shared/hooks/useToggle';
import ChangeInvestigationStatusDialog from '@customer/dialogs/change-investigation-status/ChangeInvestigationStatusDialog';

const useInvestigationStatusOptions = (): DropdownItemProps<InvestigationStatus>[] => {
  const { t } = useTranslation();

  const items = useMemo(() => {
    return Object.values(InvestigationStatus).map((value: InvestigationStatus) => {
      return {
        key: value,
        value,
        stringValue: value,
        label: t(`Enum.InvSts.${value}`, {
          ns: TranslationNamespaceBackend,
        }),
      };
    });
  }, [t]);

  return items;
};

export interface InvestigationStatusHeaderProps {
  investigationId: number;
  status: InvestigationStatus;
}

export const InvestigationStatusHeader = ({
  investigationId,
  status,
}: InvestigationStatusHeaderProps) => {
  const { t } = useTranslation();

  const items = useInvestigationStatusOptions();

  const [renderKey, setRenderKey] = useState<number>(1);
  const [statusInternal, setStatusInternal] = useState<InvestigationStatus>(status);
  const [draft, setDraft] = useState<InvestigationStatus>(status);
  const [isDialogOpen, toggleDialog] = useToggle();

  // Open the dialog with the draft status if a new status has been selected.
  const onChangeStatus = useCallback(
    (value: InvestigationStatus) => {
      if (value !== status) {
        setDraft(value);
        toggleDialog();
      }
    },
    [status, toggleDialog],
  );

  // Callback to force a re-render of the dropdown by replacing the renderKey.
  const resetDropdown = useCallback(() => {
    setRenderKey(prevKey => prevKey + 1);
  }, []);

  // Reset the dropdown value to match the current status if the dialog form is cancelled.
  const onCancel = useCallback(() => {
    setDraft(status);
    resetDropdown();
  }, [status, resetDropdown]);

  // If the status prop has changed, update the internal record and reset the draft value.
  if (statusInternal !== status) {
    setStatusInternal(status);
    setDraft(status);
    resetDropdown();
  }

  return (
    <>
      <div className={styles.container}>
        <div className={styles.label}>{t('InvestigationStatus')}</div>
        <div className={styles.dropdown}>
          <Dropdown<InvestigationStatus>
            key={renderKey}
            uid="investigation-status"
            items={items}
            maxHeight="350px" // Enough to remove the scrollbar
            value={draft}
            onChange={onChangeStatus}
          />
        </div>
      </div>

      <ChangeInvestigationStatusDialog
        investigationId={investigationId}
        status={draft}
        isOpen={isDialogOpen}
        onClose={toggleDialog}
        onCancel={onCancel}
      />
    </>
  );
};

export default InvestigationStatusHeader;
